import type { Distance, Margins, SpatialRectangle } from "@pdfking/math"
import { RemoveMargins, Scale, Subtract, Total } from "@pdfking/math"
import { ToPixel } from "@pdfking/quantity"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction, VerticalAlign } from "@pdfking/renderer-api"
import type { StringGUID } from "@pdfking/tfunc"

import { PALETTE } from "#constants"
import type { Measure } from "#internal"

import { PageMetrics } from "../layout/PageMetrics"
import { CreateRandom } from "../random/CreateRandom"
import { DrawProse } from "../text/DrawProse"
import { LoremTitle } from "../text/LoremWords"

// A wall of the box in `box`, the `index`th: on the primary a heading of its
// own over a short secondary rule and a few lines about the product, as a
// cover's flap carries them, set by the wall's own shortest side within
// `security`
export const DrawBoxWall = ({
  box,
  index,
  measure,
  security,
  seed,
}: {
  box: SpatialRectangle<Distance>
  index: number
  measure: Measure
  security: Margins<Distance>
  seed: StringGUID
}): PageShape[] => {
  const { short } = PageMetrics(box)
  const frame = RemoveMargins(box, security)
  const font_size = Scale(short, 1 / 36)
  const line_height = Scale(font_size, 1.45)
  const title_size = Scale(font_size, 1.8)
  const top = Total(frame.y, frame.height)
  const rule_top = Subtract(top, Scale(font_size, 2.6))
  const rule = Scale(short, 0.004)
  const prose_top = Subtract(rule_top, line_height)
  const prose_height = Scale(Subtract(prose_top, frame.y), 0.6)
  const [prose] = DrawProse({
    box: {
      height: prose_height,
      width: frame.width,
      x: frame.x,
      y: Subtract(prose_top, prose_height),
    },
    color: PALETTE.text,
    font_size,
    line_height,
    measure,
    random: CreateRandom(seed, "box wall", index),
  })
  return [
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.primary, position: box },
    {
      enum: PlotFunction.PLOT_TEXT,
      font_size: ToPixel(title_size),
      horizontal_align: HorizontalAlign.LEFT,
      position: {
        height: title_size,
        width: frame.width,
        x: frame.x,
        y: Subtract(top, title_size),
      },
      text: LoremTitle(CreateRandom(seed, "box wall title", index), 2),
      text_color: PALETTE.text,
      vertical_align: VerticalAlign.TOP,
    },
    {
      enum: PlotFunction.PLOT_RECT,
      fill_color: PALETTE.secondary,
      position: {
        height: rule,
        width: Scale(short, 0.06),
        x: frame.x,
        y: Subtract(rule_top, rule),
      },
    },
    ...prose,
  ]
}
